import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { TOOLS, CATEGORIES } from '@/lib/tools';
import { useSeo } from '@/lib/seo';
import { inputClass } from '@/components/calcUi';

export default function ToolsPage() {
  useSeo({
    title: '15 Free Online Calculators — Math, Money & More',
    description: 'Browse 15 free calculators for percentages, fractions, dates, loans, savings, salary, tips, BMI, currency and unit conversions. No sign-up required.',
    path: '/tools',
  });

  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [cat, setCat] = useState(params.get('cat') || 'all');

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return TOOLS.filter((t) => {
      if (cat !== 'all' && t.category !== cat) return false;
      if (!query) return true;
      return (
        t.name.toLowerCase().includes(query) ||
        t.tagline.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
      );
    });
  }, [q, cat]);

  const onSearch = (e) => {
    setQ(e.target.value);
    const next = new URLSearchParams(params);
    if (e.target.value) next.set('q', e.target.value); else next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <header className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">Free online calculators</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Search a growing library of free calculators — filter by category or type a keyword.</p>
      </header>
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={q}
            onChange={onSearch}
            placeholder="Search tools (e.g. loan, percentage, BMI)"
            data-testid="tools-search-input"
            className={inputClass + ' pl-10'}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-1">
          <button
            type="button"
            onClick={() => setCat('all')}
            data-testid="filter-cat-all"
            className={`px-4 h-11 shrink-0 rounded-lg text-sm font-medium transition-colors ${cat === 'all' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >All</button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              data-testid={`filter-cat-${c.id}`}
              className={`px-4 h-11 shrink-0 rounded-lg text-sm font-medium transition-colors ${cat === c.id ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            >{c.name}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="tools-empty">
          No tools match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`tools-list-${t.slug}`}
                className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 group-hover:text-emerald-700">{t.name}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.tagline}</p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
